import * as XLSX from 'xlsx'

/**
 * Đọc file định mức người dùng vừa chọn, để bung chi tiết từng công đoạn ngay trên bảng xem trước.
 *
 * ⚠️ Đây CHỈ để hiển thị. Việc kiểm tra hợp lệ là của service — nó đã chạy xong ở bước xem trước và
 *    là nguồn sự thật duy nhất. Đừng thêm luật kiểm tra vào đây: hai nơi kiểm riêng thì sau vài lần
 *    sửa sẽ lệch nhau, và người dùng sẽ tin vào cái sai.
 *
 * Đọc được cả .csv lẫn .xlsx nhờ `xlsx` (đã dùng sẵn ở màn Phương pháp sản xuất và Nguyên vật liệu
 * nên không phát sinh thêm dung lượng tải).
 */

/**
 * Tiêu đề cột chấp nhận hai cách viết — camelCase như file mẫu service trả, và snake_case theo SRS.
 * Khớp không phân biệt hoa thường và bỏ qua dấu gạch dưới.
 */
/**
 * Tên cột hợp lệ — khớp ĐÚNG `TEMPLATE_COLUMNS` của service, phân biệt hoa/thường.
 *
 * ⚠️ KHÔNG chuẩn hoá snake_case hay hạ chữ thường ở đây. Service so khớp header bằng tên thô
 *    (`header.includes(name)`), nên bản xem trước nhận một file mà service từ chối là dạy người dùng
 *    một hợp đồng không có thật — đúng lý do cột "Tên chấp nhận thay thế" đã bị gỡ khỏi tài liệu.
 */
const COLUMNS = [
    'stageCode',
    'skuScope',
    'skuList',
    'materialCode',
    'rate',
    'effectiveFrom',
    'effectiveTo',
    'priority',
    'verifyStatus',
    'note',
    'name',
]


const normalizeHeader = (value) => (COLUMNS.includes(`${value}`.trim()) ? `${value}`.trim() : null)

/**
 * Dấu ngăn nhiều SKU trong một ô. Nhận cả ba cho dễ nhập; service mới là nơi quyết định cuối cùng.
 *
 * ⚠️ Dấu phẩy chỉ an toàn khi ô được bọc nháy kép — `"A,B"`. File mẫu bọc nháy mọi ô nên không sao,
 *    nhưng ai sửa .csv bằng trình soạn thảo mà quên nháy thì dấu phẩy tách luôn Ô, cả dòng lệch cột.
 *    Dấu `|` không đụng gì nên vẫn là lựa chọn chắc nhất.
 */
const SKU_SEPARATOR = /[|,;]/

/**
 * Bóc CSV theo RFC 4180: ô bọc nháy kép có thể chứa dấu phẩy, xuống dòng và nháy kép nhân đôi.
 *
 * Tự bóc thay vì đưa qua `xlsx` vì thư viện đó ĐOÁN kiểu ô: `2026-09-09` bị nó nhận là ngày rồi vẽ
 * lại thành `9/9/26`, khác hẳn thứ đang nằm trong file. Với .csv thì nội dung vốn đã là văn bản,
 * đọc thẳng là khớp từng ký tự — cũng đúng cách service đang đọc.
 */
const parseCsvGrid = (text) => {
    const source = text.replace(/^\uFEFF/, '')
    const grid = []
    let row = []
    let cell = ''
    let quoted = false

    for (let i = 0; i < source.length; i += 1) {
        const char = source[i]

        if (quoted) {
            if (char === '"') {
                if (source[i + 1] === '"') {
                    cell += '"'
                    i += 1
                } else {
                    quoted = false
                }
            } else {
                cell += char
            }

            continue
        }

        if (char === '"') {
            quoted = true
        } else if (char === ',') {
            row.push(cell)
            cell = ''
        } else if (char === '\n' || char === '\r') {
            if (char === '\r' && source[i + 1] === '\n') i += 1

            row.push(cell)
            grid.push(row)
            row = []
            cell = ''
        } else {
            cell += char
        }
    }

    if (cell !== '' || row.length) {
        row.push(cell)
        grid.push(row)
    }

    return grid
}

/**
 * Đọc `File`/`Blob` qua `FileReader` khi trình duyệt chưa có `text()` / `arrayBuffer()`.
 *
 * `File` kế thừa `Blob`, và cả hai hàm kia chỉ lên từ **Safari 14**. `browserslist` của repo
 * (`>0.2%, not dead, not ie <= 11`) hiện vẫn bao Safari 12.1–13.1 và iOS 12–13, còn CRA thì không
 * polyfill. Thiếu đường lui thì `parseImportFile` ném `TypeError`, `ImportExportPage` nuốt vào
 * `catch (parseError) { setFileRows([]) }` và người dùng mất phần bung chi tiết từng dòng của bản
 * xem trước mà không có một lời giải thích nào.
 *
 * Cùng lý do và cùng khuôn với `blobToText` ở `services/api/apiProductionPlanning.js`.
 */
const readFileAs = (file, as) => {
    if (typeof file[as] === 'function') return file[as]()

    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error)

        if (as === 'arrayBuffer') {
            reader.readAsArrayBuffer(file)
        } else {
            reader.readAsText(file)
        }
    })
}

const readGrid = async (file) => {
    if (`${file.name}`.toLowerCase().endsWith('.csv')) {
        return parseCsvGrid(await readFileAs(file, 'text'))
    }

    const buffer = await readFileAs(file, 'arrayBuffer')
    // `raw: false` cho .xlsx: lấy đúng chuỗi Excel đang HIỂN THỊ, không phải số sê-ri bên dưới —
    // đó cũng là thứ người dùng nhìn thấy khi mở file ra sửa.
    const book = XLSX.read(buffer, {type: 'array', raw: false})

    // File .xlsx theo SRS đặt dữ liệu ở sheet DINH_MUC; không có thì lấy sheet đầu.
    const sheetName = book.SheetNames.includes('DINH_MUC') ? 'DINH_MUC' : book.SheetNames[0]

    if (!sheetName) return []

    return XLSX.utils.sheet_to_json(book.Sheets[sheetName], {header: 1, raw: false, defval: ''})
}

export const parseImportFile = async (file) => {
    const grid = await readGrid(file)
    const headerRow = grid[0] || []
    const columns = headerRow.map(normalizeHeader)

    return grid.slice(1)
        .map((cells, index) => {
            const record = {}

            columns.forEach((key, col) => {
                if (key) record[key] = `${cells[col] === undefined ? '' : cells[col]}`.trim()
            })

            return {
                // +2 vì bỏ dòng tiêu đề và Excel đếm từ 1 — số này phải khớp cột "Dòng" trong bảng
                // lỗi mà service trả về, để người dùng mở file ra sửa đúng chỗ.
                row: index + 2,
                stageCode: record.stageCode || '',
                skuScope: record.skuScope || '',
                skuList: (record.skuList || '').split(SKU_SEPARATOR).map((item) => item.trim()).filter(Boolean),
                // Ô materialCode nay chứa được NHIỀU mã, cùng dấu ngăn với skuList. Giữ cả chuỗi
                // thô (`materialCode`) để hiện đúng thứ người dùng gõ, và mảng đã tách để đếm/lọc.
                materialCode: record.materialCode || '',
                materialCodes: (record.materialCode || '').split(SKU_SEPARATOR).map((i) => i.trim()).filter(Boolean),
                rate: record.rate || '',
                effectiveFrom: record.effectiveFrom || '',
                priority: record.priority || '',
                verifyStatus: record.verifyStatus || '',
                note: record.note || '',
                // `|| ''` chứ không `??`: file CŨ không có cột này nên `record.name` là `undefined`,
                // và bảng xem trước phải in ô rỗng chứ không phải chữ "undefined".
                name: record.name || '',
                effectiveTo: record.effectiveTo || '',
            }
        })
        // Bỏ dòng trống hoàn toàn — Excel hay để lại vài dòng rỗng ở cuối bảng.
        .filter((row) => row.stageCode || row.rate || row.skuScope)
}
